import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiError } from "../../lib/auth";
import Seo from "../../components/Seo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const dest = loc.state?.from || "/my-account";
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(identifier.trim(), password);
      const { isDealerUser } = await import("../../lib/roles");
      nav(isDealerUser(u) ? "/dealer" : dest, { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main-content" className="bg-[var(--ssc-paper)] min-h-[70vh] py-12">
      <Seo title="Sign In | Scott Sells Chevys" description="Sign in to your Scott Sells Chevys account to save deals and view trade estimates." />
      <div className="max-w-md mx-auto px-4">
        <div className="ssc-card">
          <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[var(--ssc-blue)]">Customer Portal</div>
          <h1 className="text-3xl font-black text-[var(--ssc-navy)] mt-1">Welcome back</h1>
          <p className="text-[var(--ssc-ink-2)] mt-1 text-sm">Sign in to access your saved deals and trade estimates.</p>

          <form onSubmit={submit} className="mt-5 space-y-3" data-testid="login-form">
            <div>
              <label className="ssc-label">Username or Email</label>
              <input data-testid="login-email" className="ssc-input" type="text" value={identifier}
                     onChange={(e) => setIdentifier(e.target.value)} required autoComplete="username"
                     placeholder="you@example.com" />
            </div>
            <div>
              <label className="ssc-label">Password</label>
              <div className="relative">
                <input data-testid="login-password" className="ssc-input pr-10"
                       type={showPw ? "text" : "password"} value={password}
                       onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6b7c93] hover:text-[var(--ssc-navy)] p-1.5"
                        aria-label={showPw ? "Hide password" : "Show password"}
                        data-testid="login-show-password">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {err && <div data-testid="login-error" className="text-sm text-[var(--ssc-red)]">{err}</div>}
            <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary w-full" data-testid="login-submit">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm">
            <Link to="/forgot-password" className="text-[var(--ssc-blue)] font-semibold" data-testid="login-forgot">Forgot password?</Link>
            <Link to="/signup" className="text-[var(--ssc-blue)] font-semibold" data-testid="login-go-signup">Create account →</Link>
          </div>

          <div className="mt-6 pt-6 border-t border-[var(--ssc-line)]">
            <Link to="/dealer-login" className="text-xs text-[var(--ssc-ink-2)] hover:text-[var(--ssc-blue)]" data-testid="login-go-dealer">
              Dealer portal sign-in →
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
